// IngProjectsPage.jsx
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import IngProjectsTable from '../../../../common/IngProjects/IngProjectsTable.jsx';
import IngProjectsMyApplicationsTable from '../../../../common/IngProjects/IngProjectsMyApplicationsTable.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import {
  useGetIngProjectsQuery,
  useDeleteIngProjectMutation,
  useArchiveIngProjectMutation,
  useApproveIngProjectMutation,
  useRejectIngProjectMutation,
  useGetMyApplicationsQuery,
  useDeleteApplicationMutation,
  useGetStudentStatusQuery,
  useLockStudentMutation,
} from '../../../../../redux/services/ing_projects.jsx';
import { useGetIngProjectsSettingsQuery } from '../../../../../redux/services/ing_projects_settings.jsx';
import {
  useGetPanelStoryListQuery,
  useExportPanelStoryXlsMutation,
  useExportPanelStoryMissingXlsMutation,
} from '../../../../../redux/services/ing_projects_panel_story';
import { routes, urls } from '../../../../../utils/helpers/ApiClient.jsx';
import api from '../../../../../utils/helpers/bootstrapApi.jsx';
import {
  CollapseBlock,
  Spinner,
  QuestionModal,
} from '../../../../common/index.jsx';
import { AppBarCMS } from '../../../../basic/index.jsx';
import {
  PlusOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { Alert } from 'antd';
import TableFilter from '../../../../common/TableFilter/TableFilter.jsx';
import ApplyToProjectModal from './ApplyToProjectModal.jsx';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import {
  ROLE_ING_PROJECTS_ADMIN,
} from '../../../../../utils/constants/roles.js';

const VIEW_MODE_STORAGE_KEY = 'cms-ing-projects-view-mode';

const IngProjectsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useUserHasRole([ROLE_ING_PROJECTS_ADMIN]);
  const isTeacher = !!user?.permissions?.['ing-projects-project-guarantee'];
  const isStudent = !isAdmin && !!user?.permissions?.['ing-projects-student'];

  const [viewMode, setViewMode] = useState('active'); // 'active', 'archived', 'my', 'myApplications'
  const hasRestoredViewMode = useRef(false);
  const [applyModalVisible, setApplyModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectToArchive, setProjectToArchive] = useState(null);
  const [projectToRestore, setProjectToRestore] = useState(null);
  const [existingApplication, setExistingApplication] = useState(null);
  const [deleteApplicationId, setDeleteApplicationId] = useState(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [showPdfConfirmModal, setShowPdfConfirmModal] = useState(false);

  const handleApplyClick = (project) => {
    // Skontrolovať či študent už má prihlášku na tento projekt
    const existing = myApplications?.find(
      (app) => app.ip_project_id === project.id,
    );
    setSelectedProject(project);
    setExistingApplication(existing || null);
    setApplyModalVisible(true);
  };

  const handleArchiveProject = async (projectId, archive) => {
    try {
      const result = await archiveIngProject({
        projectId,
        archive,
      }).unwrap();

      successMessage({
        message: result?.message || (archive
          ? t('Projekt bol archivovaný.')
          : t('Projekt bol obnovený a čaká na schválenie.')),
      });
      refetch();
      resetArchive();
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  const handleApplyModalClose = () => {
    setApplyModalVisible(false);
    setSelectedProject(null);
    setExistingApplication(null);
  };

  const {
    deleteSuccess: showDeleteSuccess,
    successMessage,
    errorMessage,
    apiErrorMessage,
    errorOccurred,
  } = useToastMessages();

  // Načítať stav zamknutia študenta (len ak je študent)
  const { data: studentStatus } = useGetStudentStatusQuery(undefined, {
    skip: !isStudent,
  });
  const isStudentLocked = studentStatus?.is_locked ?? false;
  // Načítať prihlášky študenta (len ak je študent)
  const { data: myApplications } = useGetMyApplicationsQuery(undefined, {
    skip: !isStudent,
  });
  const hasSelectedProjects =
    Array.isArray(myApplications) && myApplications.length > 0;
  const maxSelectedProjectsYear = useMemo(() => {
    if (!Array.isArray(myApplications) || myApplications.length === 0) {
      return null;
    }

    const years = myApplications
      .map((application) => Number(application?.year))
      .filter((year) => Number.isFinite(year));

    if (years.length === 0) {
      return null;
    }

    return Math.max(...years);
  }, [myApplications]);

  const [lockStudent] = useLockStudentMutation();

  const downloadPdf = useCallback(async () => {
    setIsDownloadingPdf(true);
    try {
      const response = await api.get(urls.ingProjectsMyApplicationsPdf, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `IngProjekty${new Date().getFullYear()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      errorOccurred();
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [errorOccurred]);

  const handleDownloadPdf = useCallback(() => {
    if (!isStudentLocked && !hasSelectedProjects) {
      errorMessage({
        message: t('Nie je možné ukončiť výber projektov'),
        description: t('Najprv si musíte vybrať aspoň jeden projekt.'),
      });
      return;
    }

    if (isStudentLocked) {
      // Ak je už zamknutý, priamo stiahni PDF
      downloadPdf();
    } else {
      // Ak ešte nie je zamknutý, zobraz potvrdzovací modal
      setShowPdfConfirmModal(true);
    }
  }, [isStudentLocked, hasSelectedProjects, errorMessage, t, downloadPdf]);

  const handleConfirmDownloadPdf = useCallback(async () => {
    setShowPdfConfirmModal(false);
    try {
      // Najprv zamknúť študenta
      await lockStudent().unwrap();
      // Potom stiahnuť PDF
      await downloadPdf();
      successMessage({ message: t('Výber projektov bol ukončený.') });
    } catch (error) {
      apiErrorMessage(error);
    }
  }, [lockStudent, downloadPdf, successMessage, apiErrorMessage, t]);

  useEffect(() => {
    document.title = t('Inžinierske projekty') + ' | FRI UNIZA';
  }, [t]);

  const { data: settings, isLoading: isLoadingSettings } =
    useGetIngProjectsSettingsQuery();
  const { data: projects, isLoading, refetch } = useGetIngProjectsQuery();

  const teacherPersonId = user?.id;

  // Check if project modification is allowed (for teachers)
  const canModifyProjects = settings?.can_modify_projects ?? false;
  const canManageProjects = canModifyProjects || isAdmin;
  const projectModificationPeriodLabel = useMemo(() => {
    const from = settings?.date_project_modification_from;
    const to = settings?.date_project_modification_to;

    if (!from || !to) {
      return null;
    }

    const formatDate = (value) =>
      new Date(value).toLocaleDateString('sk-SK');

    return `${formatDate(from)} – ${formatDate(to)}`;
  }, [settings]);
  // Check if applying to projects is allowed (for students)
  const canApplyToProjects = settings?.can_apply_to_projects ?? false;
  // Check if panel story tab should be visible
  const canPanelStory = settings?.can_panel_story ?? false;
  const [deleteIngProject, { isSuccess: isDeleteSuccess, reset: resetDelete }] =
    useDeleteIngProjectMutation();

  const [
    archiveIngProject,
    { isLoading: isArchiving, reset: resetArchive },
  ] = useArchiveIngProjectMutation();

  const [
    approveIngProject,
    { reset: resetApprove },
  ] = useApproveIngProjectMutation();

  const [rejectIngProject, { reset: resetReject }] =
    useRejectIngProjectMutation();

  const [deleteApplication] = useDeleteApplicationMutation();

  const [exportPanelStory, { isLoading: isExportingPanelStory }] =
    useExportPanelStoryXlsMutation();
  const [exportPanelStoryMissing, { isLoading: isExportingPanelStoryMissing }] =
    useExportPanelStoryMissingXlsMutation();

  const handleExportPanelStory = async () => {
    try {
      const result = await exportPanelStory().unwrap();
      if (result?.data?.url) {
        window.open(result.data.url, '_blank');
      }
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  const handleExportPanelStoryMissing = async () => {
    try {
      const result = await exportPanelStoryMissing().unwrap();
      if (result?.data?.url) {
        window.open(result.data.url, '_blank');
      }
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  const handleApproveProject = async (projectId) => {
    try {
      const result = await approveIngProject(projectId).unwrap();
      successMessage({
        message:
          result?.message ||
          t('Projekt bol schválený.'),
      });
      refetch();
      resetApprove();
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  const handleRejectProject = async (projectId) => {
    try {
      const result = await rejectIngProject(projectId).unwrap();
      successMessage({
        message:
          result?.message ||
          t('Projekt bol zamietnutý.'),
      });
      refetch();
      resetReject();
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  // Panel Story list (for teachers and admins)
  const { data: panelStoryProjects, isLoading: isLoadingPanelStory } = useGetPanelStoryListQuery(undefined, {
    skip: viewMode !== 'panelStory' || (!isTeacher && !isAdmin),
  });

  useEffect(() => {
    if (isDeleteSuccess) {
      showDeleteSuccess();
      resetDelete(); // prevent endless toasts
    }
  }, [isDeleteSuccess, showDeleteSuccess, resetDelete]);

  // Check if user can see waiting projects (guarantor or admin)
  const canSeeWaitingProjects =
    isAdmin ||
    (teacherPersonId != null &&
      projects?.some((project) =>
        project.study_programs?.some(
          (sp) =>
            sp.guarantee === teacherPersonId && sp.pivot?.approved === null,
        ),
      ));

  // Calculate count of waiting projects
  const waitingProjectsCount = useMemo(() => {
    if (!projects) return 0;
    return projects.filter((project) => {
      if (project.archived_at != null) return false;
      if (!project.study_programs || project.study_programs.length === 0) {
        return false;
      }

      if (isAdmin) {
        return project.study_programs.some((sp) => sp.pivot?.approved == null);
      } else {
        return project.study_programs.some(
          (sp) =>
            sp.guarantee === teacherPersonId && sp.pivot?.approved === null,
        );
      }
    }).length;
  }, [projects, user, teacherPersonId, isAdmin]);

  const availableViewModes = useMemo(() => {
    const modes = [
      ...(isStudent
        ? ['myApplications']
        : isTeacher ? ['my'] : []),
      'active',
    ];

    if (canSeeWaitingProjects) {
      modes.push('waiting');
    }

    if (!isStudent) {
      modes.push('archived');
    }

    if ((isTeacher || isAdmin) && canPanelStory) {
      modes.push('panelStory');
    }

    return modes;
  }, [canPanelStory, canSeeWaitingProjects, isAdmin, isStudent, isTeacher]);

  useEffect(() => {
    if (isLoading || isLoadingSettings) {
      return;
    }

    if (hasRestoredViewMode.current) {
      return;
    }

    const storedViewMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (storedViewMode && availableViewModes.includes(storedViewMode)) {
      setViewMode(storedViewMode);
    }

    hasRestoredViewMode.current = true;
  }, [availableViewModes, isLoading, isLoadingSettings]);

  const options = [
    // Pre študentov zobraz "Vybrané projekty", pre učiteľov "Moje projekty"
    ...(isStudent
      ? [{ label: t('Vybrané projekty'), value: 'myApplications' }]
      : isTeacher ? [{ label: t('Moje aktívne projekty'), value: 'my' }] : []),
    { label: t('Aktívne projekty'), value: 'active' },
    ...(canSeeWaitingProjects
      ? [
          {
            label: t('Čakajúce na schválenie'),
            value: 'waiting',
            count: waitingProjectsCount,
          },
        ]
      : []),
    ...(!isStudent
      ? [{ label: t('Archivované projekty'), value: 'archived' }]
      : []),
    ...((isTeacher || isAdmin) && canPanelStory
      ? [{ label: t('Panel Story'), value: 'panelStory' }]
      : []),
  ];

  // Determine which projects to show based on viewMode
  const displayProjects = useMemo(() => {
    if (!projects) return [];

    if (viewMode === 'my') {
      // Projekty, kde je aktuálny učiteľ garant a projekt nie je archivovaný
      return projects.filter((project) => {
        if (!teacherPersonId || !project.teachers) return false;
        if (project.archived_at != null) return false;

        return project.teachers.some(
          (teacher) =>
            teacher.person_id === teacherPersonId &&
            teacher.pivot?.is_guarantor === 'Áno',
        );
      });
    }

    if (viewMode === 'archived') {
      return projects.filter((project) => project.archived_at != null);
    }

    if (viewMode === 'waiting') {
      return projects.filter((project) => {
        if (project.archived_at != null) return false;
        if (isAdmin) {
          // Admin vidí všetky projekty, ktoré majú aspoň jeden neschválený študijný program
          return project.study_programs.some(
            (sp) => sp.pivot?.approved === null,
          );
        } else {
          // Garant vidí len projekty, kde je garantom študijného programu a ten nie je schválený
          return project.study_programs.some(
            (sp) =>
              sp.guarantee === teacherPersonId && sp.pivot?.approved === null,
          );
        }
      });
    }

    // 'active'
    return projects.filter(
      (project) =>
        project.archived_at == null &&
        project.active === 'Áno' &&
        project.study_programs?.every((sp) => sp.pivot?.approved === 'Áno'),
    );
  }, [projects, viewMode, teacherPersonId, user]);

  // Show add button only when modification is allowed (for teachers)
  const mainActions = useMemo(() => {
    const actions = [];

    // PDF tlačidlo pre študentov v móde "Vybrané projekty"
    if (viewMode === 'myApplications') {
      actions.push({
        title: isStudentLocked
          ? t('Stiahnuť PDF prihlášku')
          : t('Ukončiť výber projektov'),
        icon: <FilePdfOutlined style={{ color: '#0e306d' }} />,
        onClick: handleDownloadPdf,
        disabled: isDownloadingPdf,
        disabledTooltip: t('Sťahovanie PDF...'),
        permission: 'ing-projects-student',
      });
    }

    // Pridať projekt pre učiteľov a admina inž. projektov
    if (canManageProjects && isTeacher) {
      actions.push({
        title: t('Pridať Inžiniersky projekt'),
        icon: <PlusOutlined />,
        onClick: () => navigate(routes.cmsIngProjectsCreate),
      });
    }

    // Export Panel Story pre adminov
    if (isAdmin && viewMode === 'panelStory') {
      actions.push({
        title: t('Export údajov pre Panel Story'),
        icon: <FileExcelOutlined />,
        onClick: handleExportPanelStory,
        disabled: isExportingPanelStory,
        permission: 'ing-projects-admin',
      });
      actions.push({
        title: t('Export nevyplnených projektov pre Panel Story'),
        icon: <FileExcelOutlined />,
        onClick: handleExportPanelStoryMissing,
        disabled: isExportingPanelStoryMissing,
        permission: 'ing-projects-admin',
      });
    }

    return actions;
  }, [
    viewMode,
    canManageProjects,
    isTeacher,
    isDownloadingPdf,
    t,
    navigate,
    handleDownloadPdf,
    isAdmin,
    isExportingPanelStory,
    isExportingPanelStoryMissing,
  ]);

  const handlePanelStoryClick = (project) => {
    navigate(routes.cmsIngProjectsPanelStory(project.id));
  };

  const handleProjectRowClick = (project) => {
    const canEditProject =
      viewMode !== 'waiting' &&
      viewMode !== 'archived' &&
      canManageProjects &&
      !stillApproving(project);

    navigate(
      canEditProject
        ? routes.cmsIngProjectsEdit(project.id)
        : routes.cmsIngProjectsDetail(project.id),
    );
  };

  const stillApproving = (project) =>
    (project.study_programs ?? []).some((sp) => sp?.pivot?.approved === null);

  if (isLoading) {
    return <Spinner />;
  }

  if (viewMode === 'panelStory' && isLoadingPanelStory) {
    return <Spinner />;
  }

  return (
    <>
      <AppBarCMS
        title={t('Projekty inž. štúdia')}
        backUrl={-1}
        backUrlTooltip={t('Späť')}
        mainActions={mainActions}
      />

      <div style={{ margin: '10px' }}>
        <TableFilter
          title={t('Filter projektov')}
          storageKey={VIEW_MODE_STORAGE_KEY}
          value={viewMode}
          setValue={setViewMode}
          options={options}
        />

        {!isLoadingSettings && isTeacher && !canModifyProjects && (
          <Alert
            style={{ marginBottom: 12 }}
            type="warning"
            showIcon
            description={
              projectModificationPeriodLabel
                ? t('Projekty aktuálne nie je možné meniť. Obdobie manažovania projektov je zvyčajne otvorené po zverejnení priradenia projektov a trvá do začiatku prihlasovania na projekty študentmi.')
                : t('Projekty aktuálne nie je možné meniť. Obdobie manažovania projektov nie je nastavené.')
            }
          />
        )}

        {isStudent && !isStudentLocked && (
          <Alert
            style={{ marginBottom: 12 }}
            type="warning"
            showIcon
            title={t('Výber projektov ešte nebol ukončený')}
            description={
              <>
                {t('Po ukončení výberu projektov je potrebné zvoliť filter "Vybrané projekty" a potvrdiť výber kliknutím na ikonu')}
                {' '}
                <FilePdfOutlined style={{ color: '#0e306d' }} />
                {' '}
                {t('v pravej hornej časti okna.')}
              </>
            }
          />
        )}

        {isStudent && isStudentLocked && (
          <Alert
            style={{ marginBottom: 12 }}
            type="success"
            showIcon
            title={t('Výber projektov bol ukončený')}
            description={
              maxSelectedProjectsYear
                ? t('Výber projektov bol úspešne ukončený (akademický rok {{from}}/{{to}}).', {
                    from: maxSelectedProjectsYear - 1,
                    to: maxSelectedProjectsYear,
                  })
                : t('Výber projektov bol úspešne ukončený')
            }
          />
        )}

        {viewMode === 'panelStory' ? (
          <CollapseBlock title={t('Panel Story')}>
            <IngProjectsTable
              projects={panelStoryProjects || []}
              isPanelStory
              isAdmin={isAdmin}
              onDetailClick={(project) =>
                navigate(routes.cmsIngProjectsDetail(project.id))
              }
              onPanelStoryClick={handlePanelStoryClick}
              panelStoryDisabled={(project) =>
                !isAdmin &&
                !project.teachers?.some(
                  (teacher) => teacher.person_id === teacherPersonId,
                )
              }
              panelStoryDisabledTooltip={t('Nie ste učiteľom tohto projektu')}
              onRowClick={handleProjectRowClick}
            />
          </CollapseBlock>
        ) : viewMode === 'myApplications' ? (
          <CollapseBlock title={t('Vybrané projekty')}>
            <IngProjectsMyApplicationsTable
              applications={myApplications || []}
              canModifyApplications={canApplyToProjects && !isStudentLocked}
              disabledTooltip={
                isStudentLocked
                  ? t('Výber projektov bol ukončený')
                  : !canApplyToProjects
                    ? t('Úprava prihlášok nie je v tomto období povolená')
                    : undefined
              }
              onDetailClick={(project) =>
                navigate(routes.cmsIngProjectsDetail(project?.id))
              }
              onEditClick={(application) => {
                setExistingApplication(application);
                setApplyModalVisible(true);
              }}
              onDeleteClick={(application) => {
                setDeleteApplicationId(application.id);
              }}
            />
          </CollapseBlock>
        ) : (
          <CollapseBlock
            title={
              viewMode === 'archived'
                ? t('Archivované projekty')
                : viewMode === 'my'
                  ? t('Moje aktívne projekty')
                  : viewMode === 'waiting'
                    ? t('Čakajúce na schválenie')
                    : t('Aktívne projekty')
            }
          >
            <IngProjectsTable
              projects={displayProjects}
              isArchived={viewMode === 'archived'}
              isWaiting={viewMode === 'waiting'}
              hideStatusColumn={['my', 'active', 'archived'].includes(viewMode)}
              isAdmin={isAdmin}
              isStudent={isStudent}
              canModifyProjects={canManageProjects}
              onDetailClick={(project) =>
                navigate(routes.cmsIngProjectsDetail(project.id))
              }
              onStudentsClick={
                isStudent || viewMode === 'waiting'
                  ? undefined
                  : (project) =>
                      navigate(routes.cmsIngProjectsStudents(project.id))
              }
              onEditClick={
                viewMode === 'waiting' || viewMode === 'archived'
                  ? undefined
                  : (project) => {
                      navigate(routes.cmsIngProjectsEdit(project.id));
                    }
              }
              secondaryCondition={(project) =>
                canManageProjects && !stillApproving(project)
              }
              editDisabledTooltip={(project) =>
                !canManageProjects
                  ? t('Úprava projektov nie je v tomto období povolená')
                  : stillApproving(project)
                    ? t(
                        'Projekt sa nedá upraviť – čaká na schválenie študijnými programami.',
                      )
                    : undefined
              }
              onDelete={
                viewMode === 'waiting' || viewMode === 'archived'
                  ? undefined
                  : (project) => deleteIngProject(project.id)
              }
              onArchiveClick={
                isStudent || viewMode !== 'my' || isArchiving
                  ? undefined
                  : (project) => setProjectToArchive(project)
              }
              onActivateClick={
                !isStudent && !isArchiving
                  ? (project) => setProjectToRestore(project)
                  : undefined
              }
              canActivateClick={(project) =>
                project.teachers?.some(
                  (teacher) =>
                    teacher.person_id === teacherPersonId &&
                    teacher.pivot?.is_guarantor === 'Áno',
                )
              }
              canApply={canApplyToProjects && !isStudentLocked}
              applyDisabledTooltip={
                isStudentLocked
                  ? t('Výber projektov bol ukončený')
                  : !canApplyToProjects
                    ? t(
                        'Prihlasovanie na projekty nie je v tomto období povolené',
                      )
                    : undefined
              }
              onApplyClick={handleApplyClick}
              onApproveClick={(project) => handleApproveProject(project.id)}
              onRejectClick={(project) => handleRejectProject(project.id)}
              onRowClick={handleProjectRowClick}
            />
          </CollapseBlock>
        )}
      </div>

      <ApplyToProjectModal
        visible={applyModalVisible}
        onClose={handleApplyModalClose}
        project={selectedProject}
        application={existingApplication}
      />

      <QuestionModal
        visible={deleteApplicationId !== null}
        toggle={() => setDeleteApplicationId(null)}
        chosenPositive={async () => {
          try {
            await deleteApplication(deleteApplicationId).unwrap();
            successMessage({ message: t('Prihláška bola úspešne zmazaná.') });
          } catch (error) {
            apiErrorMessage(error);
          } finally {
            setDeleteApplicationId(null);
          }
        }}
        headerTitle={t('Zmazať prihlášku')}
        question={t('Naozaj chcete zmazať túto prihlášku?')}
        yes={t('Áno')}
      />

      <QuestionModal
        visible={projectToArchive !== null}
        toggle={() => setProjectToArchive(null)}
        chosenPositive={async () => {
          try {
            if (!projectToArchive) {
              return;
            }

            await handleArchiveProject(projectToArchive.id, true);
          } finally {
            setProjectToArchive(null);
          }
        }}
        headerTitle={t('Archivovať projekt')}
        question={t(
          'Naozaj chcete archivovať tento projekt? Po archivovaní už nebude ponúkaný študentom.',
        )}
        yes={t('Áno')}
      />

      <QuestionModal
        visible={projectToRestore !== null}
        toggle={() => setProjectToRestore(null)}
        chosenPositive={async () => {
          try {
            if (!projectToRestore) {
              return;
            }

            await handleArchiveProject(projectToRestore.id, false);
          } finally {
            setProjectToRestore(null);
          }
        }}
        headerTitle={t('Obnoviť projekt z archívu')}
        question={t(
          'Naozaj chcete obnoviť tento projekt z archívu? Po obnovení bude opäť čakať na schválenie.',
        )}
        yes={t('Áno')}
      />

      <QuestionModal
        visible={showPdfConfirmModal}
        toggle={() => setShowPdfConfirmModal(false)}
        chosenPositive={handleConfirmDownloadPdf}
        headerTitle={t('Ukončenie výberu projektov inžinierskeho štúdia')}
        question={
          <>
            <p>
              {t(
                'Touto operáciou ukončíte výber projektov pre inžinierske štúdium a stiahnete si PDF súbor, ktorý je potrebné odovzdať na študijnom oddelení. Po stiahnutí dokumentu už nebudete schopní zmeniť svoj výber. Naozaj chcete pokračovať?',
              )}
            </p>
            <p>
              {t(
                'PDF súbor so zvolenými projektami Vám bude zaslaný ako príloha e-mailu s potvrdením výberu inž. projektov.',
              )}
            </p>
          </>
        }
        yes={t('Áno, ukončiť výber a stiahnuť PDF')}
      />
    </>
  );
};

export default IngProjectsPage;
